import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTemplate } from '@/contexts/TemplateContext'
import type { PublicProfile } from '@/types'
import { Gallery } from '@/components/gallery'
import { BookingModal } from '@/components/booking/BookingModal'

interface EstheticianTemplateProps {
    profile: PublicProfile
}

export function EstheticianTemplate({ profile }: EstheticianTemplateProps) {
    const { t } = useTranslation('public')
    const { colors, typography } = useTemplate()
    const [isBookingOpen, setIsBookingOpen] = useState(false)

    return (
        <div
            className="min-h-screen"
            style={{
                fontFamily: typography.body,
                backgroundColor: colors.colors.background,
                color: colors.colors.text,
            }}
        >
            {/* Navigation */}
            <header
                className="bg-white/70 backdrop-blur-md sticky top-0 z-50 border-b"
                style={{ borderColor: `${colors.colors.primary}11` }}
            >
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            {profile.profile_image && (
                                <img
                                    src={profile.profile_image}
                                    alt={profile.business_name}
                                    className="w-12 h-12 rounded-full object-cover"
                                    style={{ borderColor: `${colors.colors.primary}33`, borderWidth: 2 }}
                                />
                            )}
                            <div>
                                <h1 className="text-lg font-semibold">{profile.business_name}</h1>
                                {profile.tagline && (
                                    <p
                                        className="text-xs uppercase tracking-wider"
                                        style={{ color: colors.colors.textMuted }}
                                    >
                                        {profile.tagline}
                                    </p>
                                )}
                            </div>
                        </div>
                        <nav className="hidden md:flex items-center space-x-8">
                            <a
                                href="#about"
                                className="hover:opacity-80 transition-colors cursor-pointer"
                                style={{ color: colors.colors.textMuted }}
                            >
                                {t('template.beauty.about')}
                            </a>
                            <a
                                href="#services"
                                className="hover:opacity-80 transition-colors cursor-pointer"
                                style={{ color: colors.colors.textMuted }}
                            >
                                {t('template.beauty.services')}
                            </a>
                            <a
                                href="#gallery"
                                className="hover:opacity-80 transition-colors cursor-pointer"
                                style={{ color: colors.colors.textMuted }}
                            >
                                {t('template.beauty.gallery')}
                            </a>
                            <a
                                href="#contact"
                                className="hover:opacity-80 transition-colors cursor-pointer"
                                style={{ color: colors.colors.textMuted }}
                            >
                                {t('template.beauty.contact')}
                            </a>
                            <button
                                onClick={() => setIsBookingOpen(true)}
                                className="px-6 py-2 rounded-full font-medium hover:opacity-90 transition-colors cursor-pointer"
                                style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
                            >
                                {t('template.beauty.bookNow')}
                            </button>
                        </nav>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative py-20 px-4 overflow-hidden">
                <div
                    className="absolute top-0 right-0 w-1/2 h-full"
                    style={{
                        background: `linear-gradient(to left, ${colors.colors.secondary}30, transparent)`,
                    }}
                />
                <div
                    className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"
                    style={{ backgroundColor: `${colors.colors.primary}10` }}
                />
                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <p
                                className="font-medium tracking-widest uppercase mb-4 text-sm"
                                style={{ color: colors.colors.primary }}
                            >
                                Radiant Skin Awaits
                            </p>
                            <h1
                                className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6"
                                style={{ fontFamily: typography.heading }}
                            >
                                Your Journey to{' '}
                                <span className="italic" style={{ color: colors.colors.primary }}>
                                    Glowing Skin
                                </span>{' '}
                                Starts Here
                            </h1>
                            <p className="text-lg mb-8 leading-relaxed max-w-lg" style={{ color: colors.colors.textMuted }}>
                                {profile.bio || 'I believe every skin tells a story. Let me help you write yours with personalized skincare treatments that bring out your natural radiance.'}
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <a
                                    href="#contact"
                                    className="inline-flex items-center px-8 py-4 rounded-full font-semibold shadow-lg hover:opacity-90 transition-all cursor-pointer"
                                    style={{
                                        backgroundColor: colors.colors.primary,
                                        color: colors.colors.secondary,
                                    }}
                                >
                                    {t('template.beauty.bookConsultation')}
                                    <span className="ml-2">→</span>
                                </a>
                                <a
                                    href="#services"
                                    className="inline-flex items-center px-8 py-4 border-2 rounded-full font-semibold hover:opacity-90 transition-all cursor-pointer"
                                    style={{ borderColor: colors.colors.text, color: colors.colors.text }}
                                >
                                    {t('template.beauty.exploreServices')}
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                                {profile.cover_image ? (
                                    <img
                                        src={profile.cover_image}
                                        alt="Skincare Treatment"
                                        className="w-full object-cover"
                                    />
                                ) : profile.profile_image ? (
                                    <img
                                        src={profile.profile_image}
                                        alt={profile.business_name}
                                        className="w-full object-cover"
                                    />
                                ) : (
                                    <div
                                        className="w-full flex items-center justify-center"
                                        style={{ backgroundColor: colors.colors.secondary, aspectRatio: '4/5' }}
                                    >
                                        <span className="text-6xl">✨</span>
                                    </div>
                                )}
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: `linear-gradient(to top, ${colors.colors.text}80, transparent)`,
                                    }}
                                />
                                <div className="absolute bottom-6 left-6 right-6 text-white">
                                    <p className="text-sm uppercase tracking-wider mb-1">Specializing in</p>
                                    <p className="text-2xl font-semibold">
                                        {profile.tagline || 'Facials, Chemical Peels & Skincare'}
                                    </p>
                                </div>
                            </div>
                            <div
                                className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4"
                                style={{ backgroundColor: colors.colors.background }}
                            >
                                <div className="flex items-center space-x-2">
                                    <div className="flex text-yellow-400">★★★★★</div>
                                    <span className="font-semibold">5.0</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* About */}
            {profile.bio && (
                <section id="about" className="py-20 px-4 bg-white">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-16 items-center">
                            <div className="order-2 lg:order-1">
                                <p
                                    className="font-medium tracking-widest uppercase mb-4 text-sm"
                                    style={{ color: colors.colors.primary }}
                                >
                                    {t('template.beauty.aboutMe')}
                                </p>
                                <h2
                                    className="text-4xl md:text-5xl font-bold mb-6"
                                    style={{ fontFamily: typography.heading }}
                                >
                                    Passion for{' '}
                                    <span className="italic" style={{ color: colors.colors.primary }}>
                                        Healthy Skin
                                    </span>
                                </h2>
                                <div className="space-y-6 text-lg leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                    <p>{profile.bio}</p>
                                </div>
                                {profile.location && (
                                    <p className="text-sm mt-4" style={{ color: colors.colors.textMuted }}>
                                        📍 {profile.location}
                                    </p>
                                )}
                                <div className="grid grid-cols-3 gap-6 mt-10">
                                    <div className="text-center">
                                        <p className="text-4xl font-bold" style={{ color: colors.colors.primary }}>
                                            10+
                                        </p>
                                        <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                                            {t('template.beauty.yearsExperience')}
                                        </p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-4xl font-bold" style={{ color: colors.colors.primary }}>
                                            2000+
                                        </p>
                                        <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                                            {t('template.beauty.happyClients')}
                                        </p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-4xl font-bold" style={{ color: colors.colors.primary }}>
                                            15+
                                        </p>
                                        <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                                            {t('template.beauty.certifications')}
                                        </p>
                                    </div>
                                </div>
                                {profile.social_links && profile.social_links.length > 0 && (
                                    <div className="mt-8 flex items-center space-x-4">
                                        {profile.social_links.slice(0, 3).map((link) => (
                                            <a
                                                key={link.platform}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`Follow on ${link.platform}`}
                                                className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:opacity-80"
                                                style={{ backgroundColor: colors.colors.background, color: colors.colors.primary }}
                                            >
                                                {link.platform === 'instagram' && '📷'}
                                                {link.platform === 'facebook' && '📘'}
                                                {link.platform === 'tiktok' && '🎵'}
                                                {!['instagram', 'facebook', 'tiktok'].includes(link.platform) && '🔗'}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="order-1 lg:order-2 relative">
                                {profile.profile_image ? (
                                    <img
                                        src={profile.profile_image}
                                        alt={profile.business_name}
                                        className="rounded-3xl shadow-2xl w-full object-cover"
                                    />
                                ) : (
                                    <div
                                        className="rounded-3xl shadow-2xl w-full flex items-center justify-center"
                                        style={{ backgroundColor: colors.colors.secondary, aspectRatio: '3/4' }}
                                    >
                                        <span className="text-6xl">✨</span>
                                    </div>
                                )}
                                <div
                                    className="absolute -bottom-6 -left-6 rounded-2xl p-6 shadow-xl"
                                    style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
                                >
                                    <span className="text-2xl mb-2 block">📜</span>
                                    <p className="font-semibold">Licensed Esthetician</p>
                                    <p className="text-sm" style={{ color: colors.colors.secondary }}>State Certified</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Services */}
            {profile.services && profile.services.length > 0 && (
                <section
                    id="services"
                    className="py-20 px-4"
                    style={{ backgroundColor: colors.colors.background }}
                >
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16">
                            <p
                                className="font-medium tracking-widest uppercase mb-4 text-sm"
                                style={{ color: colors.colors.primary }}
                                >
                                    {t('template.beauty.treatments')}
                                </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold"
                                style={{ fontFamily: typography.heading }}
                                >
                                    {t('template.beauty.servicesAndPricing')}
                                </h2>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {profile.services.map((service) => (
                                <div
                                    key={service.id}
                                    className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow"
                                >
                                    <div
                                        className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                                        style={{ backgroundColor: colors.colors.background }}
                                    >
                                        <span style={{ color: colors.colors.primary }}>✨</span>
                                    </div>
                                    <div className="flex justify-between items-start mb-4">
                                        <h3 className="text-2xl font-semibold">{service.name}</h3>
                                        {service.price_display !== 'hidden' && service.price && (
                                            <span className="text-2xl font-bold" style={{ color: colors.colors.primary }}>
                                                ${service.price.toFixed(0)}
                                            </span>
                                        )}
                                    </div>
                                    {service.description && (
                                        <p className="mb-4" style={{ color: colors.colors.textMuted }}>
                                            {service.description}
                                        </p>
                                    )}
                                    {service.duration_minutes && (
                                        <div className="flex items-center text-sm" style={{ color: colors.colors.textMuted }}>
                                            <span className="mr-2" style={{ color: colors.colors.primary }}>⏱</span>
                                            <span>{service.duration_minutes} {t('template.common.min')}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Gallery */}
            {profile.gallery && profile.gallery.length > 0 && (
                <section id="gallery" className="py-20 px-4 bg-white">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16">
                            <p
                                className="font-medium tracking-widest uppercase mb-4 text-sm"
                                style={{ color: colors.colors.primary }}
                                >
                                    {t('template.beauty.results')}
                                </p>
                                <h2
                                    className="text-4xl md:text-5xl font-bold"
                                    style={{ fontFamily: typography.heading }}
                                >
                                    {t('template.beauty.beforeAfterGallery')}
                                </h2>
                        </div>
                        <Gallery
                            images={profile.gallery}
                            layout="grid"
                            limit={4}
                            enableLightbox={true}
                            showCaptions={false}
                            aspectRatio="square"
                            gapSize={4}
                        />
                    </div>
                </section>
            )}

            {/* Hours */}
            {profile.availability && profile.availability.length > 0 && (
                <section
                    className="py-20 px-4"
                    style={{ backgroundColor: colors.colors.background }}
                >
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <p
                                className="font-medium tracking-widest uppercase mb-4 text-sm"
                                style={{ color: colors.colors.primary }}
                                >
                                    {t('template.beauty.availability')}
                                </p>
                                <h2
                                    className="text-4xl md:text-5xl font-bold"
                                    style={{ fontFamily: typography.heading }}
                                >
                                    {t('template.beauty.studioHours')}
                                </h2>
                        </div>
                        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    {profile.availability.slice(0, 4).map((day) => (
                                        <div
                                            key={day.day_of_week}
                                            className="flex justify-between items-center py-3 border-b"
                                            style={{ borderColor: colors.colors.background }}
                                        >
                                            <span className="font-semibold">{day.day_name}</span>
                                            <span style={{ color: colors.colors.textMuted }}>
                                                {day.is_available
                                                    ? day.is_all_day
                                                        ? t('template.common.allDay')
                                                        : `${day.start_time} - ${day.end_time}`
                                                    : t('template.common.closed')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div className="space-y-4">
                                    {profile.availability.slice(4).map((day) => (
                                        <div
                                            key={day.day_of_week}
                                            className="flex justify-between items-center py-3 border-b"
                                            style={{ borderColor: colors.colors.background }}
                                        >
                                            <span className="font-semibold">{day.day_name}</span>
                                            <span
                                                style={{
                                                    color: day.is_available ? colors.colors.textMuted : colors.colors.primary,
                                                    fontWeight: day.is_available ? 'normal' : 'semibold',
                                                }}
                                            >
                                                {day.is_available
                                                    ? day.is_all_day
                                                        ? t('template.common.allDay')
                                                        : `${day.start_time} - ${day.end_time}`
                                                    : t('template.common.closed')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Contact */}
            <section
                id="contact"
                className="py-20 px-4"
                style={{ backgroundColor: colors.colors.background }}
            >
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16">
                        <div>
                            <p
                                className="font-medium tracking-widest uppercase mb-4 text-sm"
                                style={{ color: colors.colors.primary }}
                            >
                                {t('template.beauty.contact')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold mb-6"
                                style={{ fontFamily: typography.heading }}
                            >
                                Let's Transform Your Skin
                            </h2>
                            <p className="text-lg mb-8 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                Ready to achieve the skin you've always wanted? Book your consultation today and let's create your personalized skincare journey.
                            </p>
                            <div className="space-y-6">
                                {profile.location && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow"
                                            style={{ backgroundColor: colors.colors.background }}
                                        >
                                            <span style={{ color: colors.colors.primary }}>📍</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold">{t('template.beauty.location')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.location}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.phone && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow"
                                            style={{ backgroundColor: colors.colors.background }}
                                        >
                                            <span style={{ color: colors.colors.primary }}>📞</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold">{t('template.beauty.phone')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.phone}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.email && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow"
                                            style={{ backgroundColor: colors.colors.background }}
                                        >
                                            <span style={{ color: colors.colors.primary }}>✉️</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold">{t('template.beauty.email')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.email}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {profile.social_links && profile.social_links.length > 0 && (
                                <div className="mt-8">
                                    <h4 className="font-semibold mb-4">{t('template.beauty.followMyJourney')}</h4>
                                    <div className="flex space-x-4">
                                        {profile.social_links.slice(0, 3).map((link) => (
                                            <a
                                                key={link.platform}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`Follow on ${link.platform}`}
                                                className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:opacity-80 shadow"
                                                style={{ backgroundColor: colors.colors.background, color: colors.colors.primary }}
                                            >
                                                {link.platform === 'instagram' && '📷'}
                                                {link.platform === 'facebook' && '📘'}
                                                {link.platform === 'tiktok' && '🎵'}
                                                {!['instagram', 'facebook', 'tiktok'].includes(link.platform) && '🔗'}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Contact CTA */}
                        <div className="flex flex-col justify-center">
                            <div className="bg-white rounded-3xl p-8 shadow-xl">
                                <h3 className="text-2xl font-semibold mb-6">{t('template.beauty.bookYourAppointment')}</h3>
                                <div className="flex flex-col gap-3">
                                    {profile.phone && (
                                        <a
                                            href={`tel:${profile.phone}`}
                                            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold text-lg shadow-lg hover:opacity-90 transition-all"
                                            style={{
                                                backgroundColor: colors.colors.primary,
                                                color: colors.colors.secondary,
                                            }}
                                        >
                                            📞 {t('template.beauty.callNow')}
                                        </a>
                                    )}
                                    {profile.email && (
                                        <a
                                            href={`mailto:${profile.email}`}
                                            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold text-lg shadow-lg hover:opacity-90 transition-all"
                                            style={{
                                                backgroundColor: colors.colors.secondary,
                                                color: colors.colors.primary,
                                            }}
                                        >
                                            ✉️ {t('template.beauty.emailMe')}
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4" style={{ backgroundColor: colors.colors.text, color: colors.colors.background }}>
                <div className="max-w-7xl mx-auto text-center">
                    {profile.profile_image && (
                        <img
                            src={profile.profile_image}
                            alt={profile.business_name}
                            className="w-16 h-16 rounded-full mx-auto mb-4 border-2"
                            style={{ borderColor: colors.colors.primary }}
                        />
                    )}
                    <h3 className="text-2xl font-semibold mb-2">{profile.business_name}</h3>
                    {profile.tagline && (
                        <p className="mb-6" style={{ color: colors.colors.background }}>
                            {profile.tagline}
                        </p>
                    )}
                    {profile.social_links && profile.social_links.length > 0 && (
                        <div className="flex justify-center space-x-4 mb-6">
                            {profile.social_links.slice(0, 3).map((link) => (
                                <a
                                    key={link.platform}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Follow on ${link.platform}`}
                                    className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:opacity-80"
                                    style={{ backgroundColor: colors.colors.text, color: colors.colors.background }}
                                >
                                    {link.platform === 'instagram' && '📷'}
                                    {link.platform === 'facebook' && '📘'}
                                    {!['instagram', 'facebook'].includes(link.platform) && '🔗'}
                                </a>
                            ))}
                        </div>
                    )}
                    <p style={{ color: `${colors.colors.background}99` }}>
                        © {new Date().getFullYear()} {profile.business_name}. {t('template.beauty.poweredBy')}
                    </p>
                </div>
            </footer>

            {isBookingOpen && profile.services && profile.services.length > 0 && (
                <BookingModal
                    isOpen={isBookingOpen}
                    onClose={() => setIsBookingOpen(false)}
                    profile={profile}
                    services={profile.services}
                    categories={[]}
                />
            )}
        </div>
    )
}

export default EstheticianTemplate;
